/**
 * The way back from the layer documentation (#875): »Zemljevid« and »Začetna stran« in the docs
 * header.
 *
 * The docs URL names no app — the start page (`/d/<app>`) links a bare `/si/docs` — so the app
 * comes from, in this order:
 *  1. the referrer, when it is one of our own app pages (`/d/<app_…>` or `/map?sxapp=<app_…>`):
 *     where the reader came from. Kept per tab (`sessionStorage`), so a reload, an in-page link
 *     and the SI · CZ switch (a full load whose referrer is the docs page) keep it;
 *  2. else the half's own map — the app carrying `wiki-cad-root-si|cz`, the capability the docs
 *     already use for their KingCAD tree (0680) — from `/me/sxapps`, which answers anonymous
 *     readers too.
 * Identity by public id only, told apart by SHAPE (`publicIdRefs.ts`): a slug never steers it.
 */

import { isAppPublicId } from '../../shared/utils/publicIdRefs.js';
import type { DocsCountry } from './docsRouter.js';

/** Per-tab memory of the app the reader came from. */
export const DOCS_BACK_APP_KEY = 'gsx8_docs_back_app';

/** An app of `/me/sxapps`, as far as the docs need it. */
export interface DocsApp {
  appId: string;
  capabilities: readonly string[];
}

export interface DocsBackLinks {
  mapHref: string;
  homeHref: string;
}

/** The app public id of one of our own app pages, or null (another site, a slug, anything else). */
export function appFromReferrer(referrer: string, origin: string): string | null {
  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  const onStartPage = /^\/d\/([^/]+)/.exec(url.pathname);
  let ref: string | null = null;
  if (onStartPage?.[1]) {
    try {
      ref = decodeURIComponent(onStartPage[1]);
    } catch {
      return null;
    }
  } else if (url.pathname === '/map') {
    ref = url.searchParams.get('sxapp');
  }
  return isAppPublicId(ref) ? ref : null;
}

/** The map of a docs half: the app carrying `wiki-cad-root-<country>`; null when none is listed. */
export function docsMapAppFor(apps: readonly DocsApp[], country: DocsCountry): string | null {
  const capability = `wiki-cad-root-${country}`;
  const app = apps.find((a) => isAppPublicId(a.appId) && a.capabilities.includes(capability));
  return app?.appId ?? null;
}

export function docsBackLinks(appId: string): DocsBackLinks {
  const id = encodeURIComponent(appId);
  return { mapHref: `/map?sxapp=${id}`, homeHref: `/d/${id}` };
}

/** The remembered app of this tab; storage can be blocked (private mode, a policy) — then null. */
export function readBackApp(): string | null {
  try {
    const value = window.sessionStorage.getItem(DOCS_BACK_APP_KEY);
    return isAppPublicId(value) ? value : null;
  } catch {
    return null;
  }
}

export function storeBackApp(appId: string): void {
  try {
    window.sessionStorage.setItem(DOCS_BACK_APP_KEY, appId);
  } catch {
    // blocked storage: the link still works on this page load
  }
}
